import { darken, lighten, parseToHsl } from 'polished';
import { scaleLightness } from '../../../design-tokens/colors';
import { setAlpha } from '../../../design-tokens';
import { DesignTokens } from '../../../theme';

/**
 * Return Table override tokens.
 */
export const getTableOverrideTokens = (dt: DesignTokens) => {
  let table = {};
  if (dt.overrides.table.borderColor) {
    table = {
      bd: dt.overrides.table.borderColor,
      'header-bottom-bd': dt.overrides.table.borderColor,
      'frozen-column-divider-color': scaleLightness(-0.2, dt.overrides.table.borderColor),
    };
  }

  let header = {};
  if (dt.overrides.table.headerLabelColor) {
    header = { 'header-fg': dt.overrides.table.headerLabelColor };
  }
  if (dt.overrides.table.headerBackground) {
    header = { ...header, 'header-bg': dt.overrides.table.headerBackground };
  }
  if (dt.overrides.table.headerBorderRightColor) {
    header = { ...header, 'header-right-bd': dt.overrides.table.headerBorderRightColor };
  }

  let row = {};
  if (dt.overrides.table.textColor) {
    row = {
      'cell-fg': dt.overrides.table.textColor,
      'cell-hover-fg': dt.overrides.table.textColor,
      'cell-pressed-fg': dt.overrides.table.textColor,
      'cell-selected-fg': dt.overrides.table.textColor,
      'cell-selected-inactive-fg': dt.overrides.table.textColor,
    };
  }
  if (dt.overrides.table.rowBackgroundColorOdd) {
    row = {
      ...row,
      'row-bg': dt.overrides.table.rowBackgroundColorOdd,
      'row-hover-bg': setAlpha(dt.overrides.table.rowBackgroundColorOdd, 0.2),
      'row-pressed-bg':
        dt.themeVariant === 'high-contrast'
          ? dt.overrides.table.rowBackgroundColorOdd
          : setAlpha(dt.overrides.table.rowBackgroundColorOdd, 0.25),
      'row-selected-bg':
        dt.themeVariant === 'high-contrast'
          ? dt.overrides.table.rowBackgroundColorOdd
          : setAlpha(dt.overrides.table.rowBackgroundColorOdd, 0.25),
      'row-selected-inactive-bg':
        dt.themeVariant === 'high-contrast'
          ? dt.overrides.table.rowBackgroundColorOdd
          : setAlpha(dt.overrides.table.rowBackgroundColorOdd, 0.2),
    };
  }
  if (dt.overrides.table.rowBackgroundColorEven) {
    row = { ...row, 'alternate-row-bg': dt.overrides.table.rowBackgroundColorEven };
  }
  if (dt.overrides.table.cellBorderColor) {
    row = { ...row, 'cell-bd': dt.overrides.table.cellBorderColor };
  }

  return {
    ...table,
    ...header,
    ...row,
    ...getCustomTableStateOverrides(
      dt.overrides.table.textColor,
      dt.overrides.table.rowBackgroundColorOdd
    ),
  };
};

/**
 * Calculate custom foreground and background colors for state patterns
 * (normal/hover/pressed/selected/selected inactive).
 */
const getCustomTableStateOverrides = (fg?: string, bg?: string) => {
  if (!fg || !bg) return {};
  const fullAmount = 100;
  const primaryLightenShiftFactor = 1.6949;
  const primaryDarkenShiftFactor = 2.5;
  let cellFg = {};
  if (!!fg) {
    const fgLightness = parseToHsl(fg).lightness * 100;
    cellFg = {
      'cell-fg': fg,
      'cell-hover-fg': lighten(
        (((fullAmount - fgLightness) / fullAmount) * (10 * primaryLightenShiftFactor)) / 100,
        fg
      ),
      'cell-pressed-fg': darken(
        (((fullAmount - fgLightness) / fullAmount) * (4 * primaryDarkenShiftFactor)) / 100,
        fg
      ),
      'cell-selected-fg': darken(
        (((fullAmount - fgLightness) / fullAmount) * (4 * primaryDarkenShiftFactor)) / 100,
        fg
      ),
      'cell-selected-inactive-fg': darken(
        (((fullAmount - fgLightness) / fullAmount) * (10 * primaryDarkenShiftFactor)) / 100,
        fg
      ),
    };
  }
  let cellBg = {};
  if (!!bg) {
    const bgLightness = parseToHsl(fg).lightness * 100;
    cellBg = {
      'row-bg': bg,
      'row-hover-bg': darken(
        ((bgLightness / fullAmount) * (2 * primaryDarkenShiftFactor)) / 100,
        bg
      ),
      'row-pressed-bg': darken(
        ((bgLightness / fullAmount) * (4 * primaryDarkenShiftFactor)) / 100,
        bg
      ),
      'row-selected-bg': darken(
        ((bgLightness / fullAmount) * (4 * primaryDarkenShiftFactor)) / 100,
        bg
      ),
      'row-selected-inactive-bg': darken(
        ((bgLightness / fullAmount) * (2 * primaryDarkenShiftFactor)) / 100,
        bg
      ),
    };
  }
  return { ...cellFg, ...cellBg };
};
